// Notification plugin.
import { api, reducer, name } from '../../notifications/interface'
import events from '../../notifications/interface/events'
import * as pushRegistrationSagas from './sagas/pushRegistration'
import { enableWebsocketChannelEntry, processNotification } from './sagas/notification'
import createMiddleware from '../middleware'

// Other plugins.
import { mapEvents } from '../../events/interface/actions'
import { update as updateConfig } from '../../config/interface/actions'

// Libraries.
import { put } from 'redux-saga/effects'
import { defaultsDeep, values } from 'lodash/fp'

// Parse and/or Validate
import { validation as v8n, parse, enums } from '../../../src/common/validation'

/**
 * Configuration options for the notification feature.
 * @public
 * @name config.notifications
 * @memberof config
 * @instance
 * @param {Object} notifications - The notifications configuration object.
 * @param {number} [notifications.idCacheLength=100] - Default amount of event ids to remember for de-duplication purposes.
 * @param {string} [notifications.incomingCallNotificationMode='any-channel'] - Communication channel mode used for incoming call notifications. Supported values are 'any-channel' or 'push-channel-only'.
 * @param {Object} [notifications.pushRegistration] - Object describing the server to use for push services.
 * @param {string} [notifications.pushRegistration.server] - Hostname for the push registration server.
 * @param {string} [notifications.pushRegistration.port] - Port for the push registration server.
 * @param {string} [notifications.pushRegistration.protocol] - Protocol for the push registration server.
 * @param {string} [notifications.pushRegistration.version] - Version for the push registration server.
 */

const defaultOptions = {
  idCacheLength: 100,
  incomingCallNotificationMode: 'any-channel'
}

// config validation
const v8nValidation = v8n.schema({
  idCacheLength: v8n.positive(),
  incomingCallNotificationMode: enums(['any-channel', 'push-channel-only']),
  pushRegistration: v8n.optional(
    v8n.schema({
      server: v8n.string(),
      port: v8n.string(),
      protocol: v8n.string(),
      version: v8n.string()
    })
  )
})
const parseOptions = parse('notifications', v8nValidation)

/**
 * Notifications plugin factory.
 * @method notifications
 * @param {configs.notifications} options - Configuration options for authentication.
 * @return {Object} plugin - A notifications plugin.
 */
export default function notifications (options = {}, bottle) {
  const pluginOptions = defaultsDeep(defaultOptions, options)
  parseOptions(pluginOptions)

  function * init () {
    yield put(updateConfig(pluginOptions, name))
    yield put(mapEvents(events))
  }

  const capabilities = ['push', 'registerPushNotifications', 'externalNotifications']

  return {
    name,
    capabilities,
    init,
    api,
    reducer,
    sagas: [enableWebsocketChannelEntry, processNotification, ...values(pushRegistrationSagas)],
    middleware: createMiddleware(bottle)
  }
}
